import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { cloneDeep } from "lodash";
//actions
import {
  setTabErrors,
  recordChangeObject,
  logChangedKey,
} from "@actions/global";
import {
  setNewClients,
  deleteNewClients,
  setDeletedClients,
  loadConfig,
  loadModifiedSchemas,
  setClientVisibility,
} from "@features/schema-management/actions";
// components
import InputField from "@components/ui/InputField";
import { sortTriangle } from "@assets/images";
import IconRefresh from "@assets/icons/IconRefresh";
import IconTrash from "@assets/icons/IconTrash";
// utils
import { logDiffs } from "@features/schema-management/utils/schemaManagementHelpers";
import { isEmptyObject } from "@utils/object-helpers";
// constants
import {
  SCHEDULE_DRAFT_SAVE_STATE,
  SCHEDULE_DRAFT_STATUS,
  ACTION_TAB,
} from "@constants/schema-management";

const EditableLabel = (props) => {
  const {
    id,
    label,
    value,
    placeholder,
    required,
    disabled,
    onChange,
    name,
    ...rest
  } = props;
  const dispatch = useDispatch();
  const featureName = useSelector((state) => state.schemaAllView.featureName);
  const isAthosConfig = useSelector(
    (state) => state.schemaAllView.isAthosConfig
  );
  const validationErrors = useSelector(
    (state) => state.schemaAllView.validationErrors
  );
  const errors = useSelector((state) => state.schemaAllView.errors);
  const clientSchema = useSelector((state) => state.schema.clientSchema);
  const existingClients = useSelector(
    (state) => state.schemaAllView.existingClients
  );
  const clientVisibility = useSelector(
    (state) => state.schemaAllView.clientVisibility
  );
  const deletedClients = useSelector(
    (state) => state.schemaAllView.deletedClients
  );
  const { actionTab } = useSelector((state) => state.schemaAllView);
  const schemaManagementConfig = useSelector(
    (state) => state.schemaAllView.schemaManagementConfig
  );
  const modifiedSchema = useSelector((state) => state.schema.modifiedSchema);
  const newClients = useSelector((state) => state.schemaAllView.newClients);
  const changeDict = useSelector((state) => state.schemaAllView.changeDict);
  const initialConfig = useSelector(
    (state) => state.schemaAllView.initialConfig
  );
  const schemaConfigSaveState = useSelector(
    (state) => state.schemaConfigScheduler.schemaConfigSaveState
  );
  const scheduleDraft = useSelector(
    (state) => state.schemaConfigScheduler.scheduleDraft
  );
  const localLabel = isAthosConfig ? value.toLowerCase() : value;
  const editLabel =
    !isAthosConfig &&
    Object.values(newClients).some((client) => client === value);
  const isLabel = value && !editLabel;
  const isDeleted =
    deletedClients.hasOwnProperty(localLabel) &&
    deletedClients[localLabel] === null;
  const isError =
    Object.keys(validationErrors).includes(localLabel) ||
    Object.keys(errors).some((key) => key.split(".")[0] === localLabel);
  const newClientNameLimit = 100;
  let isScheduled = scheduleDraft.status === SCHEDULE_DRAFT_STATUS.SCHEDULED;
  let scheduledClients = scheduleDraft.sectionsModified;

  useEffect(() => {
    if (!isAthosConfig) {
      const initialClients = initialConfig.filter((config) => config?.client).map((config) =>
        config?.client?.toLowerCase()
      );
      if (
        schemaConfigSaveState === SCHEDULE_DRAFT_SAVE_STATE.EDIT ||
        !isLabel
      ) {
        if (Number(id.split(".")[1]) > initialClients.length - 1) {
          handleInputError(value);
        }
      }
    }
  }, [initialConfig]);

  const handleInputError = (inputValue) => {
    let isARepeatingNewClient =
      newClients && !isEmptyObject(newClients)
        ? Object.keys(newClients)?.filter(
          (key) =>
            newClients[key]?.toLowerCase() === inputValue?.toLowerCase() &&
            key !== id
        ).length
        : false;
    let isDuplicate =
      !!existingClients?.filter(
        (input) => input.toLowerCase() === inputValue?.toLowerCase()
      ).length || isARepeatingNewClient;
    isDuplicate = isScheduled
      ? isARepeatingNewClient ||
      (isDuplicate && !scheduledClients?.includes(inputValue))
      : !!isDuplicate;

    let newClientNameValidation = {};
    newClientNameValidation[id] = {
      newClientNameCannotBeBlank: !inputValue,
      inputLimitError: inputValue?.length > 100,
      duplicateClient: isDuplicate,
    };
    dispatch(setNewClients(id, inputValue));
    dispatch(setTabErrors({ ...validationErrors, ...newClientNameValidation }));
  };

  const handleRemoveClient = (value) => {
    let updatedConfig = cloneDeep(schemaManagementConfig);
    let updatedChanges = {};
    if (newClients.hasOwnProperty(value)) {
      let updatedSchema = cloneDeep(modifiedSchema);
      delete updatedConfig[value];
      delete updatedSchema.properties[value];
      dispatch(loadModifiedSchemas(updatedSchema));
      dispatch(deleteNewClients(value));
      updatedChanges = logDiffs(initialConfig, updatedConfig, featureName);
    } else {
      setClientVisibility(false);
      let updatedDeletedClients = { ...deletedClients, [value]: null };
      if (initialConfig.hasOwnProperty(value)) {
        updatedConfig[value] = initialConfig[value];
        updatedChanges = {
          ...updatedDeletedClients,
          ...logDiffs(initialConfig, updatedConfig, featureName),
        };
      } else {
        delete updatedConfig[value];
        updatedChanges = {
          ...logDiffs(initialConfig, updatedConfig, featureName),
        };
      }
      dispatch(setDeletedClients(updatedDeletedClients));
    }
    dispatch(loadConfig(updatedConfig));
    dispatch(recordChangeObject(updatedChanges));
    dispatch(logChangedKey(Object.keys(updatedChanges)));
  };

  const handleRefreshClient = (value) => {
    setClientVisibility(true);
    const { [value]: _, ...updatedDeletedClients } = deletedClients;
    let updatedChanges = cloneDeep(changeDict);
    delete updatedChanges[value];
    dispatch(setDeletedClients(updatedDeletedClients));
    dispatch(recordChangeObject(updatedChanges));
    dispatch(logChangedKey(Object.keys(updatedChanges)));
  };

  const handleClientVisibility = () => {
    const updateVisibility = {
      ...clientVisibility,
      [localLabel]: !clientVisibility[localLabel],
    };
    dispatch(setClientVisibility(updateVisibility));
  };

  return isLabel ? (
    <>
      <div
        className={`override font-bold text-lg-2 shadow-lg bg-white my-2 rounded-md p-6 h-20 flex justify-between ${isDeleted || !clientVisibility[localLabel] ? "hide-sibling" : ""
          }
          ${isError || isDeleted ? "!text-[#ff0000]" : ""}`}
      >
        <div className="flex">
          {value?.toUpperCase()}
          {isAthosConfig && !isDeleted && (
            <img
              src={
                !clientVisibility[localLabel]
                  ? sortTriangle.up
                  : sortTriangle.down
              }
              className="h-max my-auto mx-2 cursor-pointer"
              onClick={() => {
                handleClientVisibility();
              }}
            />
          )}
        </div>
        <div className="mx-0 my-auto cursor-pointer">
          {isAthosConfig &&
            (isDeleted ? (
              <IconRefresh
                className="mr-2"
                iconFillColor="#34657f"
                onClick={() => {
                  handleRefreshClient(localLabel);
                }}
              />
            ) : (
              <IconTrash
                iconFillColor="#34657f"
                onClick={() => handleRemoveClient(localLabel)}
              />
            ))}
        </div>
      </div>
    </>
  ) : (
    <InputField
      labelText="New Client Name"
      className="override my-6"
      key={`supplemental-name-${id}-${name}`}
      inputName={`supplemental-name-${name}`}
      inputValue={value}
      inputLimit={newClientNameLimit}
      onChangeCallback={(value) => {
        onChange(value);
        handleInputError(value);
      }}
      placeholderText={placeholder}
      errorPresent={
        (required && !value) ||
        validationErrors[id]?.newClientNameCannotBeBlank ||
        validationErrors[id]?.inputLimitError ||
        !!validationErrors[id]?.duplicateClient
      }
      inputTypeOfErrors={[
        validationErrors[id]?.newClientNameCannotBeBlank && "required",
        validationErrors[id]?.inputLimitError && "limit",
      ]}
      errorMessageText={
        (validationErrors[id]?.newClientNameCannotBeBlank &&
          "Field is required.") ||
        (!!validationErrors[id]?.duplicateClient &&
          "Client name either already exists or it is currently scheduled.") ||
        (validationErrors[id]?.inputLimitError &&
          "This field cannot exceed 100 characters.")
      }
      disabled={disabled}
    />
  );
};
export default EditableLabel;
